import { CssBaseline, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import ScrollToTop from 'components/ScrollToTop';
import envConfig from 'configs/envConfig';
import { ConfigContext } from 'context';
import { getPartnerConfiguration, getPartnerName } from 'partner_configuration';
import { lazy, Suspense, useEffect } from 'react';
import CacheBuster from 'react-cache-buster';
import { Route, Routes } from 'react-router-dom';
import AppThemeProvider from 'themes/AppThemeProvider';
// import AccountDeletionRequest from 'views/AccountDeletion/request';
// import VerifyDeleteRequest from 'views/AccountDeletion/verifyRequest';
// import { ActiveDevices } from 'views/ActiveDevices/ActiveDevices';
// import AppBridge from 'views/AppBridge/AppBridge';
// import AuthorizeFromApp from 'views/AuthorizeFromApp';
// import AutoLogin from 'views/AutoLogin/AutoLogin';
// import { Billings } from 'views/Billings/Billings';
// import Bridge from 'views/Bridge';
// import BuySubscription from 'views/BuySubscription/BuySubscription';
// import CancellationRefundPolicy from 'views/CancellationRefundPolicy/CancellationRefundPolicy';
// import CheckoutSession from 'views/CheckoutSession/CheckoutSession';
// import NotFound from 'views/NotFound/NotFound';
// import PairingSuccess from 'views/PairingSuccess/PairingSuccess';
// import PaymentSuccess from 'views/PaymentStatus/PaymentStatus';
// import PlanList from 'views/PlanList';
// import PlanListInterceptor from 'views/PlanList/PlanListInterceptor';
// import SelectDevice from 'views/SelectDevice';
// import SetupComplete from 'views/SetupComplete/SetupComplete';
// import SetupInit from 'views/SetupInit';
// import SubscriptionFunctions from 'views/SubscriptionFunctions/subscriptionFunctions';
// import PaymentOptions from 'views/SubscriptionInfo/componenets/PaymentOptions';
// import { SubscriptionDetails } from 'views/SubscriptionInfo/SubscriptionDetails';

import InstaLoader from 'components/LoaderComponent/InstaLoader';
import { NAVIGATION_ROUTES } from 'constant/index';
// import { UserInfo } from 'views/UserInfo/UserInfo';
// import WhySkip from 'views/WhySkip';
// import Promotion from 'views/WhySubscribe/Promotion';
// import SkippingTrial from 'views/WhySubscribe/SkippingTrial';
import appPackageInfo from '../package.json';
import './App.css';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
// import LoginScreen from './views/LoginScreen/LoginScreen';

// Pairing flow
const SetupCompleteLazy = lazy(
  () => import('views/SetupComplete/SetupComplete')
);
const SetupInitLazy = lazy(() => import('views/SetupInit'));
const PairingSuccessLazy = lazy(
  () => import('views/PairingSuccess/PairingSuccess')
);

const CancellationRefundPolicyLazy = lazy(
  () => import('views/CancellationRefundPolicy/CancellationRefundPolicy')
);
const CheckoutSessionLazy = lazy(
  () => import('views/CheckoutSession/CheckoutSession')
);
const SelectDeviceLazy = lazy(() => import('views/SelectDevice'));
const SubscriptionFunctionsLazy = lazy(
  () => import('views/SubscriptionFunctions/subscriptionFunctions')
);
const PaymentSuccessLazy = lazy(
  () => import('views/PaymentStatus/PaymentStatus')
);
const PlanListLazy = lazy(() => import('views/PlanList'));
const PlanListInterceptorLazy = lazy(
  () => import('views/PlanList/PlanListInterceptor')
);
const PaymentOptionsLazy = lazy(
  () => import('views/SubscriptionInfo/componenets/PaymentOptions')
);
const BillingsLazy = lazy(() => import('views/Billings/Billings'));
const BuySubscriptionLazy = lazy(
  () => import('views/BuySubscription/BuySubscription')
);
const SubscriptionDetailsLazy = lazy(
  () => import('views/SubscriptionInfo/SubscriptionDetails')
);
const ActiveDevicesLazy = lazy(
  () => import('views/ActiveDevices/ActiveDevices')
);

// Promotion and Skip Trial/ Feature Routes
const PromotionLazy = lazy(() => import('views/WhySubscribe/Promotion'));
const SkippingTrialLazy = lazy(
  () => import('views/WhySubscribe/SkippingTrial')
);
const WhySkipLazy = lazy(() => import('views/WhySkip'));

// Auth pages
const LoginScreenLazy = lazy(() => import('views/LoginScreen/LoginScreen'));
const AutoLoginLazy = lazy(() => import('views/AutoLogin/AutoLogin'));

// Account Deletion
const AccountDeletionRequestLazy = lazy(
  () => import('views/AccountDeletion/request')
);
const VerifyDeleteRequestLazy = lazy(
  () => import('views/AccountDeletion/verifyRequest')
);

// Bridge
const BridgeLazy = lazy(() => import('views/Bridge'));
const AppBridgeLazy = lazy(() => import('views/AppBridge/AppBridge'));
const AuthorizeFromAppLazy = lazy(() => import('views/AuthorizeFromApp'));

const UserInfoLazy = lazy(() => import('views/UserInfo/UserInfo'));
const NotFoundLazy = lazy(() => import('views/NotFound/NotFound'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const url: any = window.location.origin;
  console.log('url::', url);
  const partnerName = getPartnerName(url);
  console.log(':: partnerName', partnerName);

  const config = getPartnerConfiguration(url);
  const theme = AppThemeProvider(partnerName);

  useEffect(() => {
    const env = envConfig('env');
    if (!env) return;
    if (env.includes('luna')) {
      document.title = 'Luna Baby';
      const favicon = document.querySelector("link[rel~='icon']");
      favicon?.setAttribute(
        'href',
        `${process.env.PUBLIC_URL}/favicon_luna.ico`
      );
    }
  }, []);

  return (
    <CacheBuster
      currentVersion={appPackageInfo.version}
      isEnabled={process.env.NODE_ENV === 'production'}
      isVerboseMode
      loadingComponent={<div>Loading...</div>}
    >
      <div className="App" style={{ height: '100vh' }}>
        <Sentry.ErrorBoundary>
          <ErrorBoundary>
            <ScrollToTop />
            <ConfigContext.Provider value={config}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Suspense fallback={<InstaLoader />}>
                  <SentryRoutes>
                    <Route
                      path={NAVIGATION_ROUTES.LOGIN}
                      element={<LoginScreenLazy />}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.AUTO_LOGIN}
                      element={<AutoLoginLazy />}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.AUTHORIZE_FROM_APP}
                      element={<AuthorizeFromAppLazy />}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.REQUEST_DELETE_SERVICES}
                      element={<AccountDeletionRequestLazy />}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.VERIFY_DELETE_SERVICES}
                      element={<VerifyDeleteRequestLazy />}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.APP_BRIDGE}
                      Component={AppBridgeLazy}
                    />
                    <Route
                      path={NAVIGATION_ROUTES.BRIDGE}
                      Component={BridgeLazy}
                    />
                    <Route element={<PrivateRoute />}>
                      {/* Promotion and Skip Trial/ Feature Routes */}
                      <Route
                        path={NAVIGATION_ROUTES.PROMOTION}
                        element={<PromotionLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.SKIP_TRIAL}
                        element={<SkippingTrialLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.WHY_SKIP}
                        element={<WhySkipLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.DEFAULT}
                        element={<ActiveDevicesLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.ACTIVE_DEVICES}
                        element={<ActiveDevicesLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.MY_ACCOUNT}
                        element={<UserInfoLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.MY_SUBSCRIPTION}
                        element={<SubscriptionDetailsLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.BILLINGS}
                        element={<BillingsLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.BUY_SUBSCRIPTION}
                        element={<BuySubscriptionLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.CHECKOUT_SESSION}
                        Component={CheckoutSessionLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.PLAN_LIST}
                        Component={PlanListLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.PLAN_LIST_INTERCEPTOR}
                        element={<PlanListInterceptorLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.PAYMENT_SELECTIONS}
                        element={<PaymentOptionsLazy />}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.PAYMENT_SUCCESS}
                        Component={PaymentSuccessLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.PAIRING_SUCCESS}
                        Component={PairingSuccessLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.SETUP_INIT}
                        Component={SetupInitLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.SETUP_COMPLETE}
                        Component={SetupCompleteLazy}
                      />
                      <Route
                        path={NAVIGATION_ROUTES.SELECT_DEVICE}
                        Component={SelectDeviceLazy}
                      />
                      {/* <Route path="/promotion" element={<Features />} /> */}
                      <Route
                        path={NAVIGATION_ROUTES.SUBSCRIPTION_FUNCTIONS}
                        element={<SubscriptionFunctionsLazy />}
                      />

                      {/* <Route
                      path={NAVIGATION_ROUTES.CAMERA_SELECTION}
                      element={<CameraSelection />}
                    /> */}
                      {/* <Route
                      path="/subscription-summary"
                      element={<SubscriptionSummary />}
                    /> */}
                      <Route
                        path={NAVIGATION_ROUTES.CANCELLATION_REFUND_POLICY}
                        Component={CancellationRefundPolicyLazy}
                      />

                      <Route
                        path={NAVIGATION_ROUTES.OTHER_ROUTES}
                        Component={NotFoundLazy}
                      />
                    </Route>
                    <Route
                      path={NAVIGATION_ROUTES.OTHER_ROUTES}
                      Component={LoginScreenLazy}
                    />
                  </SentryRoutes>
                </Suspense>
              </ThemeProvider>
            </ConfigContext.Provider>
          </ErrorBoundary>
        </Sentry.ErrorBoundary>
      </div>
    </CacheBuster>
  );
}

export default App;
